import type { InterviewGame, InterviewGameAction } from '@/types/api';

/**
 * Fun & Games: what a game message shows. Its card (the game, the round, the
 * heading, dice, a Time Machine year) and the ways to answer it under the
 * interviewer's message.
 *
 * A game runs through the same turn loop as any interview; each interviewer
 * message may carry `game`, and every answer is still plain text the server
 * parses (the same words work over SMS). So the buttons are only shortcuts:
 * tapping a choice or an action sends its text exactly as if it had been
 * typed and sent.
 */

/** Skips the current round. The action bar's Skip sends it on game messages
 *  (it sends "pass" otherwise). */
export const GAME_SKIP_REPLY = 'skip';
/** Ends the game early, on every channel. */
export const GAME_DONE_REPLY = "I'm done";

export interface GameResultView {
  title: string;
  /** Null until someone has a vote. */
  winner: string | null;
  /** "3 of 5 votes", or null before anyone has voted. */
  votes: string | null;
}

export interface GameCardView {
  name: string;
  /** "Round 2 of 3", or null when the payload carries no usable round count. */
  progress: string | null;
  title: string | null;
  /** Story Dice: the rolled words, shown as tiles. */
  dice: string[];
  /** Time Machine: "1978 · age 12". */
  era: string | null;
  /** Tap-to-answer options, sent as the answer text exactly. */
  choices: string[];
  /** Secondary buttons. Skip is left to the action bar, which already has one,
   *  and "I'm done" is added while the game is running. */
  actions: InterviewGameAction[];
  /** A photo is the best answer: attaching one is the primary action. */
  wantsPhoto: boolean;
  /** FINISH: "+5 bonus credits", or null when there's no bonus. */
  bonus: string | null;
  /** Family Awards group votes (REVEAL / FINISH). */
  results: GameResultView[];
  /** FINISH: the game is over, nothing left to answer. */
  finished: boolean;
}

function isBlank(value: unknown): boolean {
  return typeof value !== 'string' || value.trim() === '';
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/** Reply text reduced to its letters, so "I’m done", "I'm done!" and "im done"
 *  are the same reply. */
function replyKey(reply: string): string {
  return reply.toLowerCase().replace(/[^a-z]/g, '');
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function roundProgress(round: unknown, totalRounds: unknown): string | null {
  if (!isCount(round) || !isCount(totalRounds) || round < 1 || totalRounds < 1) return null;
  return `Round ${Math.min(Math.floor(round), Math.floor(totalRounds))} of ${Math.floor(totalRounds)}`;
}

function eraLabel(year: unknown, age: unknown): string | null {
  const parts = [isCount(year) && year > 0 ? String(year) : null, isCount(age) ? `age ${age}` : null];
  const present = parts.filter((part): part is string => part !== null);
  return present.length > 0 ? present.join(' · ') : null;
}

function isUsableAction(action: InterviewGameAction | null | undefined): boolean {
  if (!action || isBlank(action.label) || isBlank(action.reply)) return false;
  return replyKey(action.reply) !== GAME_SKIP_REPLY;
}

function gameActions(game: InterviewGame): InterviewGameAction[] {
  const actions = (game.actions ?? []).filter(isUsableAction);
  const running = game.step !== 'INTRO' && game.step !== 'FINISH';
  const canEnd = actions.some((action) => replyKey(action.reply) === replyKey(GAME_DONE_REPLY));
  if (!running || canEnd) return actions;
  return [...actions, { label: GAME_DONE_REPLY, reply: GAME_DONE_REPLY }];
}

function votesLabel(votes: number | null | undefined, total: number): string | null {
  if (!isCount(total) || total < 1) return null;
  return `${votes ?? 0} of ${plural(total, 'vote')}`;
}

/** The card for one message's `game`, or null when the message isn't part of
 *  a game (or the payload is too thin to show one). */
export function gameCardView(game: InterviewGame | null | undefined): GameCardView | null {
  if (!game || isBlank(game.name)) return null;
  const finished = game.step === 'FINISH';
  const bonusCredits = game.bonusCredits ?? 0;
  const hasBonus = finished && isCount(bonusCredits) && bonusCredits > 0;
  const title = isBlank(game.title) ? null : game.title!.trim();
  return {
    name: game.name.trim(),
    progress: roundProgress(game.round, game.totalRounds),
    // A Time Machine heading that is just the year would repeat the era line.
    title: title === String(game.year) ? null : title,
    dice: (game.dice ?? []).filter((word) => !isBlank(word)).map((word) => word.trim()),
    era: eraLabel(game.year, game.age),
    choices: (game.choices ?? []).filter((choice) => !isBlank(choice)),
    actions: gameActions(game),
    wantsPhoto: !!game.wantsPhoto && !finished,
    bonus: hasBonus ? `+${plural(bonusCredits, 'bonus credit')}` : null,
    results: (game.results ?? [])
      .filter((result) => !!result && !isBlank(result.title))
      .map((result) => ({
        title: result.title.trim(),
        winner: isBlank(result.winner) ? null : result.winner!.trim(),
        votes: votesLabel(result.votes, result.total),
      })),
    finished,
  };
}

/** Whether anything shows under the message: ways to answer while the game
 *  runs, and the bonus or the group's results once there are some. */
export function gameAnswersShown(view: GameCardView, ended: boolean): boolean {
  const answering = !ended && !view.finished;
  const canAnswer = view.wantsPhoto || view.choices.length > 0 || view.actions.length > 0;
  return (answering && canAnswer) || view.results.length > 0 || !!view.bonus;
}
